import React, { lazy } from 'react';

import { Navigate, Route } from 'react-router-dom';

import {
  CONTEXT_MODE_ELEMENTARY,
  CONTEXT_MODE_SECONDARY,
} from 'components/Common/LayoutV2/utils';

import { STUDENT_SCOPE } from 'constants/gradeYears';

import Protected from 'containers/Protected';

import {
  DASHBOARD,
  EL_VIRTUAL_TOURS,
  VIRTUAL_TOURS,
  EVENT_SCHEDULE,
} from 'routes/constants';

import RedirectHelper from 'routes/CounselorRoutes/RedirectHelper';

import CredentialWallet from 'modules/CredentialWallet/routes';
import { MODULE_NAME as ELEMENTARY_MODULE_NAME } from 'modules/ElementaryCheckins/constants';

const CoursePlannerListingContainer = lazy(
  () =>
    import(
      /* webpackChunkName: 'course-planner-listing-container' */ 'modules/CoursePlannerListing/containers/CoursePlannerListingContainer'
    ),
);

const InitiativePage = lazy(
  () =>
    import(/* webpackChunkName: 'initiative-page' */ 'modules/InitiativePage'),
);
const Checkins = lazy(
  () =>
    import(
      /* webpackChunkName: 'elementary-checkins' */ 'modules/ElementaryCheckins'
    ),
);
const CollegeAppConfig = lazy(
  () =>
    import(
      /* webpackChunkName: 'college-app-config' */ 'modules/CollegeAppConfig'
    ),
);
const ElementaryStudentCasefile = lazy(
  () =>
    import(
      /* webpackChunkName: 'elementary-student-casefile' */ 'modules/ElementaryStudentCasefile'
    ),
);
const ElementaryStaffDashboard = lazy(
  () =>
    import(
      /* webpackChunkName: 'elementary-staff-dashboard' */ 'modules/ElementaryStaffDashboard/pages/Dashboard'
    ),
);

const ElementaryStudentCasefileUnselected = lazy(
  () =>
    import(
      /* webpackChunkName: 'elementary-student-casefile-unselected' */ 'modules/ElementaryStudentCasefileUnselected'
    ),
);
const SeniorSurvey = lazy(
  () => import(/* webpackChunkName: 'senior-survey' */ 'modules/SeniorSurvey'),
);

const IndicatorVerification = lazy(
  () =>
    import(
      /* webpackChunkName: 'indicator-verification' */ 'modules/IndicatorVerification'
    ),
);

const RosterAndTagManagement = lazy(
  () =>
    import(
      /* webpackChunkName: 'roster-and-tag-management' */ 'modules/RosterAndTagManagement'
    ),
);

const LessonLibrary = lazy(
  () =>
    import(/* webpackChunkName: 'lesson-library' */ 'modules/LessonLibrary'),
);

const AccountSettings = lazy(
  () =>
    import(
      /* webpackChunkName: 'account-settings' */ 'modules/AccountSettings'
    ),
);
const AnalyticsHighlights = lazy(
  () =>
    import(
      /* webpackChunkName: 'analytics-highlights' */ 'modules/AnalyticsHighlights'
    ),
);
const IndustryPartnerAnalytics = lazy(
  () =>
    import(
      /* webpackChunkName: 'analytics-highlights' */ 'modules/IndustryPartnerAnalytics'
    ),
);
const BYOPSponsorView = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-program-views' */ 'modules/IndustryPartnerProgramExploration/pages/BYOPSponsorView'
    ),
);
const StudentGoals = lazy(
  () => import(/* webpackChunkName: 'student-goals' */ 'modules/StudentGoals'),
);
const StudentCasefile = lazy(
  () => import(/* webpackChunkName: 'casefile' */ 'modules/StudentCasefile'),
);
const CareerCenterPages = lazy(
  () =>
    import(
      /* webpackChunkName: 'career-center' */ 'modules/CareerCenter/routes'
    ),
);
const CareerInterestAnalytics = lazy(
  () =>
    import(
      /* webpackChunkName: 'career-interest-analytics' */ 'modules/CareerInterestAnalytics'
    ),
);
const CourseCatalog = lazy(
  () =>
    import(/* webpackChunkName: 'course-catalog' */ 'modules/CourseCatalog'),
);
const CoursePlanner = lazy(
  () =>
    import(/* webpackChunkName: 'course-planner' */ 'modules/CoursePlanner'),
);

const DigitalBadges = lazy(
  () =>
    import(/* webpackChunkName: 'digital-badges' */ 'modules/DigitalBadges'),
);
const MessageCenter = lazy(
  () =>
    import(/* webpackChunkName: 'message-center' */ 'modules/MessageCenter'),
);
const Notifications = lazy(
  () =>
    import(
      /* webpackChunkName: 'notifications' */ 'modules/Notifications/pages/Notifications'
    ),
);
const Opportunities = lazy(
  () => import(/* webpackChunkName: 'opportunities' */ 'modules/Opportunities'),
);
const StaffPersonalizedPlans = lazy(
  () =>
    import(
      /* webpackChunkName: 'staff-personalized-plans' */ 'modules/StaffPersonalizedPlans'
    ),
);
const StudentPlanListing = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-plan-listing' */ 'modules/StudentPlanListing'
    ),
);
const StudentProfile = lazy(
  () =>
    import(/* webpackChunkName: 'student-profile' */ 'modules/StudentProfile'),
);
const VisitNotification = lazy(
  () =>
    import(
      /* webpackChunkName: 'visit-notification' */ 'modules/VisitNotification'
    ),
);
const PersonalizedPlans = lazy(
  () =>
    import(
      /* webpackChunkName: 'personalized-plans' */ 'modules/PersonalizedPlans'
    ),
);
const ScholarshipManagement = lazy(
  () =>
    import(
      /* webpackChunkName: 'scholarship-management' */ 'modules/ScholarshipManagement'
    ),
);
const OpportunityManagement = lazy(
  () =>
    import(
      /* webpackChunkName: 'opportunity-management' */ 'modules/OpportunityManagement'
    ),
);
const ActivityTaskExperience = lazy(
  () =>
    import(
      /* webpackChunkName: 'activity-task-experience' */ 'modules/ActivityTaskExperience'
    ),
);

const DataIntegration = lazy(
  () =>
    import(
      /* webpackChunkName: 'data-integration' */ 'modules/DataIntegration'
    ),
);

const EmployerShowcase = lazy(
  () =>
    import(
      /* webpackChunkName: 'employer-showcase' */ 'modules/EmployerShowcase'
    ),
);
const StudentPathways = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-pathways' */ 'modules/StudentPathways'
    ),
);
const ShowcaseProfile = lazy(
  () =>
    import(
      /* webpackChunkName: 'showcase-profile' */ 'modules/EmployerShowcase/components/ShowcaseProfile'
    ),
);
const SchoolProfile = lazy(
  () =>
    import(/* webpackChunkName: 'school-profile' */ 'modules/SchoolProfile'),
);

const LifeSimulator = lazy(
  () =>
    import(/* webpackChunkName: 'life-simulator' */ 'modules/LifeSimulator'),
);
const LearningTasks = lazy(
  () =>
    import(/* webpackChunkName: 'learning-tasks' */ 'modules/LearningTasks'),
);

const SchoolSearch = lazy(
  () => import(/* webpackChunkName: 'school-search' */ 'modules/SchoolSearch'),
);
const SurveyAnalytics = lazy(
  () =>
    import(
      /* webpackChunkName: 'survey-analytics' */ 'modules/SurveyAnalytics'
    ),
);

const AccountabilitySystemSetup = lazy(
  () =>
    import(
      /* webpackChunkName: 'accountability-system-setup' */ 'modules/AccountabilitySystemSetup/K12Admin/routes'
    ),
);

const AccountabilitySystemAnalytics = lazy(
  () =>
    import(
      /* webpackChunkName: 'accountability-system-analytics' */ 'modules/AccountabilitySystemAnalytics/K12Admin/routes'
    ),
);

const KeyReadinessIndicators = lazy(
  () =>
    import(
      /* webpackChunkName: 'key-readiness-indicators' */ 'modules/KeyReadinessIndicators/routes'
    ),
);
const CollegeApplicationManager = lazy(
  () =>
    import(
      /* webpackChunkName: 'application-manager' */ 'modules/CollegeApplicationManager/routes'
    ),
);
const DistrictSettings = lazy(
  () =>
    import(
      /* webpackChunkName: 'district-settings' */ 'modules/DistrictSettings'
    ),
);

const EventScheduler = lazy(
  () =>
    import(
      /* webpackChunkName: 'event-scheduler' */ 'modules/EventSchedulerV2/K12Admin'
    ),
);
const StaffDashboard = lazy(
  () =>
    import(
      /* webpackChunkName: 'staff-dashboard' */ 'modules/StaffDashboard/pages/Landing'
    ),
);

const CollegeCosts = lazy(
  () =>
    import(
      /* webpackChunkName: 'college-costs' */ 'modules/CollegeCosts/routes'
    ),
);

const RecordCenter = lazy(
  () =>
    import(
      /* webpackChunkName: 'record-center' */ 'modules/RecordCenter/K12Admin'
    ),
);
const Alumni = lazy(
  () => import(/* webpackChunkName: 'alumni' */ 'modules/Alumni'),
);
const ReportCenter = lazy(
  () =>
    import(
      /* webpackChunkName: 'report-center' */ 'modules/ReportCenter/routes'
    ),
);
const AlumniTakingSurveys = lazy(
  () =>
    import(
      /* webpackChunkName: 'alumni-taking-surveys' */ 'modules/AlumniTakingSurveys/routes'
    ),
);

const SeniorSurveyAnalytics = lazy(
  () =>
    import(
      /* webpackChunkName: 'senior-survey-analytics' */ 'modules/SeniorSurveyAnalytics'
    ),
);

const RecommendationLetterPortal = lazy(
  () =>
    import(
      /* webpackChunkName: 'recommendation-letter-portal' */ 'modules/CollegeApplicationManager/pages/RecommendationLetterPortal'
    ),
);
const ScholarshipMatching = lazy(
  () =>
    import(
      /* webpackChunkName: 'scholarship-matching' */ 'modules/ScholarshipMatching/pages/ScholarshipMatchPage'
    ),
);
const CounselorMeetingScheduler = lazy(
  () =>
    import(
      /* webpackChunkName: 'counsellor-meeting-scheduler' */ 'modules/CounselorMeetingScheduler'
    ),
);

const SupervisorExperienceTrackingPage = lazy(
  () =>
    import(
      /* webpackChunkName: 'supervisor-experience-tracking' */ 'modules/ExperienceTracking/pages/SupervisorExperienceTrackingPage'
    ),
);

const StaffExperienceTrackingPage = lazy(
  () =>
    import(
      /* webpackChunkName: 'staff-experience-tracking' */ 'modules/ExperienceTracking/pages/StaffExperienceTrackingPage'
    ),
);
const AwardLetterVerification = lazy(
  () =>
    import(
      /* webpackChunkName: 'award-letter-verification' */ 'modules/AwardLetterVerification'
    ),
);

const IndustryPartnerCRM = lazy(
  () =>
    import(
      /* webpackChunkName: 'industry-partner-crm' */ 'modules/IndustryPartnerCRM'
    ),
);
const IndustryPartnerProgramManagement = lazy(
  () =>
    import(
      /* webpackChunkName: 'k12-admin-industry-partner-program-management' */ 'modules/IndustryPartnerProgramManagement/K12Admin/routes'
    ),
);

const FAFSAReportingAndTracking = lazy(
  () =>
    import(
      /* webpackChunkName: 'fafsa-reporting-and-tracking' */ 'modules/FAFSAReportingAndTracking'
    ),
);

const IPPEK12AdminRoutes = lazy(
  () =>
    import(
      /* webpackChunkName: 'ippe-k12admin-routes' */ 'modules/IndustryPartnerProgramExploration/K12Admin/routes'
    ),
);

const StaffProgramsDashboard = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-program-views' */ 'modules/IndustryPartnerProgramExploration/pages/StaffProgramsDashboard'
    ),
);

const SurveyBankManagement = lazy(
  () =>
    import(
      /* webpackChunkName: 'survey-bank-management' */ 'modules/SurveyBankManagement'
    ),
);

const ConfigurationStatusReport = lazy(
  () =>
    import(
      /* webpackChunkName: 'configuration-status-report' */ 'modules/ConfigurationStatusReport'
    ),
);

const StaffAccountManagement = lazy(
  () =>
    import(
      /* webpackChunkName: 'staff-account-management' */ 'modules/StaffAccountManagement'
    ),
);

const StudentList = lazy(
  () => import(/* webpackChunkName: 'student-list' */ 'modules/StudentList'),
);

const OrgRoutes = lazy(
  () => import(/* webpackChunkName: 'org' */ 'routes/MemberRoutes/OrgRoutes'),
);

const PublicEventRedirect = lazy(
  () =>
    import(
      /* webpackChunkName: 'public-event-redirect' */ 'modules/EventSchedulerV2/Public/pages/PublicEventRedirect'
    ),
);

const MobileFileUpload = lazy(
  () =>
    import(
      /* webpackChunkName: 'mobile-file-upload' */ 'modules/MobileFileUpload/MobileFileUpload'
    ),
);

const VirtualTours = lazy(
  () => import(/* webpackChunkName: 'virtual-tours' */ 'modules/VirtualTours'),
);

const SurveyCompletion = lazy(
  () =>
    import(
      /* webpackChunkName: 'survey-completion' */ 'modules/Surveys/routes'
    ),
);

const BragSheetSurvey = lazy(
  () =>
    import(
      /* webpackChunkName: 'brag-sheet-survey' */ 'modules/BragSheetSurvey'
    ),
);

const CTEProgramsDashboard = lazy(
  () =>
    import(
      /* webpackChunkName: 'cte-status-dashboard' */ 'modules/CTEProgramsDashboard/K12Admin/routes'
    ),
);

const CollegeAthleticEligibility = lazy(
  () =>
    import(
      /* webpackChunkName: 'sitemap' */ 'modules/KeyReadinessIndicators/pages/CollegeAthleticEligibility'
    ),
);

const Sitemap = lazy(
  () =>
    import(
      /* webpackChunkName: 'sitemap' */ 'modules/AccountSettings/components/Sitemap'
    ),
);

const Automations = lazy(
  () =>
    import(
      /* webpackChunkName: 'automations' */ 'modules/Automations/K12Admin/pages'
    ),
);

const CounselorRoutes = () => (
  <>
    <Route path="/file-upload" element={<MobileFileUpload />} />
    <Route
      path="/public-event-redirect/:eventId/:districtId"
      element={<PublicEventRedirect />}
    />
    <Route
      path="/:studentId/senior-survey"
      element={
        <Protected userType="k12_admin">
          <SeniorSurvey />
        </Protected>
      }
    />
    <Route
      path="/automation"
      element={
        <Protected userType="k12_admin">
          <Automations />
        </Protected>
      }
    />
    <Route
      path="/scope-and-sequence-verification/*"
      element={
        <Protected userType="k12_admin">
          <IndicatorVerification />
        </Protected>
      }
    />
    <Route
      path="/el/rosters/*"
      element={
        <Protected userType="k12_admin">
          <RosterAndTagManagement isElementary />
        </Protected>
      }
    />
    <Route
      path="/rosters/*"
      element={
        <Protected userType="k12_admin">
          <RosterAndTagManagement />
        </Protected>
      }
    />
    <Route
      path="/lesson-library/*"
      element={
        <Protected userType="k12_admin">
          <LessonLibrary />
        </Protected>
      }
    />
    <Route
      path={`/el/${ELEMENTARY_MODULE_NAME}/*`}
      element={
        <Protected userType="k12_admin">
          <Checkins studentScope={STUDENT_SCOPE.ELEMENTARY} />
        </Protected>
      }
    />
    <Route
      path="/checkins/*"
      element={
        <Protected userType="k12_admin">
          <Checkins studentScope={STUDENT_SCOPE.SECONDARY} />
        </Protected>
      }
    />
    <Route
      path="/el/survey-analytics/*"
      element={
        <Protected userType="k12_admin">
          <SurveyAnalytics mode={CONTEXT_MODE_ELEMENTARY} />
        </Protected>
      }
    />
    <Route
      path="/el/:casefileName/:studentId/*"
      element={
        <Protected userType="k12_admin">
          <ElementaryStudentCasefile />
        </Protected>
      }
    />
    <Route
      path="/college-app-config/*"
      element={
        <Protected userType="k12_admin">
          <CollegeAppConfig />
        </Protected>
      }
    />
    <Route
      path="/initiative-page/:pageName"
      element={
        <Protected userType="k12_admin">
          <InitiativePage />
        </Protected>
      }
    />
    <Route
      path="/el/dashboard"
      element={
        <Protected userType="k12_admin">
          <ElementaryStaffDashboard />
        </Protected>
      }
    />
    <Route
      path="/el/casefile/"
      element={
        <Protected userType="k12_admin">
          <ElementaryStudentCasefileUnselected />
        </Protected>
      }
    />
    <Route
      path={DASHBOARD}
      element={
        <Protected userType="k12_admin">
          <StaffDashboard />
        </Protected>
      }
    />
    <Route path="/el/student-list">
      <Route
        path=":featureName"
        element={
          <Protected userType="k12_admin">
            <StudentList isElementary />
          </Protected>
        }
      />
      <Route
        index
        element={
          <Protected userType="k12_admin">
            <StudentList isElementary />
          </Protected>
        }
      />
    </Route>
    <Route path="/student-list">
      <Route
        path=":featureName"
        element={
          <Protected userType="k12_admin">
            <StudentList />
          </Protected>
        }
      />
      <Route
        index
        element={
          <Protected userType="k12_admin">
            <StudentList />
          </Protected>
        }
      />
    </Route>
    <Route
      path="/course-plans/:studentId"
      element={
        <Protected userType="k12_admin">
          <StudentPlanListing />
        </Protected>
      }
    />
    <Route
      path="/course-plans"
      element={
        <Protected userType="k12_admin">
          <CoursePlannerListingContainer />
        </Protected>
      }
    />
    <Route
      path="/course-planner-setup/*"
      element={
        <Protected userType="k12_admin">
          <CourseCatalog />
        </Protected>
      }
    />
    <Route
      path="/course-planner/:studentPlanUUID/:requirementUUID"
      element={
        <Protected userType="k12_admin">
          <CoursePlanner />
        </Protected>
      }
    />
    <Route
      path="/careers/*"
      element={
        <Protected userType="k12_admin">
          <CareerCenterPages />
        </Protected>
      }
    />
    <Route
      path="/el/career-interest-analytics"
      element={
        <Protected userType="k12_admin">
          <CareerInterestAnalytics />
        </Protected>
      }
    />
    <Route
      path="/el/plans/*"
      element={
        <Protected userType="k12_admin">
          <PersonalizedPlans mode={CONTEXT_MODE_ELEMENTARY} />
        </Protected>
      }
    />
    <Route
      path="/plans/*"
      element={
        <Protected userType="k12_admin">
          <PersonalizedPlans />
        </Protected>
      }
    />
    <Route
      path="/task-experience/*"
      element={
        <Protected userType="k12_admin">
          <ActivityTaskExperience />
        </Protected>
      }
    />
    <Route
      path="/el/learning/:studentId/*"
      element={
        <Protected userType="k12_admin">
          <LearningTasks isElementary />
        </Protected>
      }
    />
    <Route
      path="/learning/:studentId/*"
      element={
        <Protected userType="k12_admin">
          <LearningTasks />
        </Protected>
      }
    />
    <Route
      path="/goals/*"
      element={
        <Protected userType="k12_admin">
          <StudentGoals />
        </Protected>
      }
    />
    <Route
      path="/opportunities/*"
      element={
        <Protected userType="k12_admin">
          <Opportunities />
        </Protected>
      }
    />
    <Route path="/employer-showcase">
      <Route
        path=":employerId"
        element={
          <Protected userType="k12_admin">
            <ShowcaseProfile />
          </Protected>
        }
      />
      <Route
        index
        element={
          <Protected userType="k12_admin">
            <EmployerShowcase />
          </Protected>
        }
      />
    </Route>
    <Route
      path="/student-pathways/*"
      element={
        <Protected userType="k12_admin">
          <StudentPathways />
        </Protected>
      }
    />
    <Route
      path="/scholarship-management/*"
      element={
        <Protected userType="k12_admin">
          <ScholarshipManagement />
        </Protected>
      }
    />
    <Route
      path="/opportunity-management/*"
      element={
        <Protected userType="k12_admin">
          <OpportunityManagement />
        </Protected>
      }
    />
    <Route
      path="/el/staff-account-management/*"
      element={
        <Protected userType="k12_admin">
          <StaffAccountManagement isElementary />
        </Protected>
      }
    />
    <Route
      path="/staff-account-management/*"
      element={
        <Protected userType="k12_admin">
          <StaffAccountManagement />
        </Protected>
      }
    />
    <Route
      path="/account-settings/*"
      element={
        <Protected userType="k12_admin">
          <AccountSettings />
        </Protected>
      }
    />
    <Route
      path="/notifications"
      element={
        <Protected userType="k12_admin">
          <Notifications />
        </Protected>
      }
    />
    <Route
      path="/visit-notification/:notificationId"
      element={
        <Protected userType="k12_admin">
          <VisitNotification />
        </Protected>
      }
    />
    <Route
      path="/el/scope-and-sequence-setup/*"
      element={
        <Protected userType="k12_admin">
          <AccountabilitySystemSetup isElementary />
        </Protected>
      }
    />
    <Route
      path="/scope-and-sequence-setup/*"
      element={
        <Protected userType="k12_admin">
          <AccountabilitySystemSetup />
        </Protected>
      }
    />
    <Route
      path="/el/scope-and-sequence-analytics/*"
      element={
        <Protected userType="k12_admin">
          <AccountabilitySystemAnalytics isElementary />
        </Protected>
      }
    />
    <Route
      path="/el/scope-and-sequence-setup/*"
      element={
        <Protected userType="k12_admin">
          <AccountabilitySystemSetup />
        </Protected>
      }
    />
    <Route
      path="/scope-and-sequence-analytics/*"
      element={
        <Protected userType="k12_admin">
          <AccountabilitySystemAnalytics />
        </Protected>
      }
    />
    <Route
      path="/senior-survey-analytics"
      element={
        <Protected userType="k12_admin">
          <SeniorSurveyAnalytics />
        </Protected>
      }
    />
    <Route
      path="/survey-analytics/*"
      element={
        <Protected userType="k12_admin">
          <SurveyAnalytics mode={CONTEXT_MODE_SECONDARY} />
        </Protected>
      }
    />
    <Route
      path={`${EL_VIRTUAL_TOURS}/*`}
      element={
        <Protected userType="k12_admin">
          <VirtualTours isElementary />
        </Protected>
      }
    />
    <Route
      path={`${VIRTUAL_TOURS}/*`}
      element={
        <Protected userType="k12_admin">
          <VirtualTours />
        </Protected>
      }
    />
    <Route
      path="surveys/completion/:surveyInstanceId/:studentId"
      element={
        <Protected userType="k12_admin">
          <SurveyCompletion />
        </Protected>
      }
    />
    <Route
      path="/messages/*"
      element={
        <Protected userType="k12_admin">
          <MessageCenter />
        </Protected>
      }
    />
    <Route
      path="/students/:studentId/profile"
      element={
        <Protected userType="k12_admin">
          <StudentProfile mode="view" type="student" fetchUserProfile />
        </Protected>
      }
    />
    <Route
      path="/members/:memberId/profile"
      element={<StudentProfile mode="view" type="member" />}
    />
    <Route
      path="/schools/*"
      element={
        <Protected userType="k12_admin">
          <SchoolSearch />
        </Protected>
      }
    />
    <Route
      path="/analytics"
      element={
        <Protected userType="k12_admin">
          <AnalyticsHighlights />
        </Protected>
      }
    />
    <Route
      path="/industry-partner-analytics"
      element={
        <Protected userType="k12_admin">
          <IndustryPartnerAnalytics />
        </Protected>
      }
    />
    <Route
      path="/el/personalized-plans"
      element={
        <Protected userType="k12_admin">
          <StaffPersonalizedPlans mode={CONTEXT_MODE_ELEMENTARY} />
        </Protected>
      }
    />
    <Route
      path="/personalized-plans"
      element={
        <Protected userType="k12_admin">
          <StaffPersonalizedPlans />
        </Protected>
      }
    />
    <Route
      path="/badges/*"
      element={
        <Protected userType="k12_admin">
          <DigitalBadges />
        </Protected>
      }
    />
    <Route
      path="/public-placement/:placementHashId/:programId"
      element={<RedirectHelper isBYOPRedirect />}
    />
    <Route
      path="/program-management/*"
      element={
        <Protected userType="k12_admin">
          <IndustryPartnerProgramManagement />
        </Protected>
      }
    />
    <Route
      path="/game-of-life/:studentId/:scenarioUuid"
      element={
        <Protected userType="k12_admin">
          <LifeSimulator />
        </Protected>
      }
    />
    <Route
      path="/fafsa-tracker"
      element={
        <Protected userType="k12_admin">
          <FAFSAReportingAndTracking />
        </Protected>
      }
    />
    <Route path="/school-profile">
      <Route
        path=":schoolId"
        element={
          <Protected userType="k12_admin">
            <SchoolProfile />
          </Protected>
        }
      />
      <Route
        path=":schoolId/:schoolName"
        element={
          <Protected userType="k12_admin">
            <SchoolProfile />
          </Protected>
        }
      />
    </Route>
    <Route
      path="/data-upload/*"
      element={
        <Protected userType="k12_admin">
          <DataIntegration />
        </Protected>
      }
    />
    <Route
      path="/key-readiness-indicators/*"
      element={
        <Protected userType="k12_admin">
          <KeyReadinessIndicators />
        </Protected>
      }
    />
    <Route
      path="/college-applications/*"
      element={
        <Protected userType="k12_admin">
          <CollegeApplicationManager />
        </Protected>
      }
    />
    <Route
      path="/district-settings"
      element={
        <Protected userType="k12_admin">
          <DistrictSettings />
        </Protected>
      }
    />
    {/* college-costs is reserved for students only, but its sub-paths should be allowed */}
    <Route
      path="/college-costs"
      element={
        <Protected userType="k12_admin">
          <Navigate to={DASHBOARD} />
        </Protected>
      }
    />
    <Route
      path="/college-costs/*"
      element={
        <Protected userType="k12_admin">
          <CollegeCosts />
        </Protected>
      }
    />
    <Route
      path="/record-requests/*"
      element={
        <Protected userType="k12_admin">
          <RecordCenter isCounselor />
        </Protected>
      }
    />
    <Route
      path="/alumni/survey/:isPreview/*"
      element={<AlumniTakingSurveys />}
    />
    <Route
      path="/alumni/*"
      element={
        <Protected userType="k12_admin">
          <Alumni />
        </Protected>
      }
    />
    <Route
      path="/el/report-center"
      element={
        <Protected userType="k12_admin">
          <ReportCenter mode={CONTEXT_MODE_ELEMENTARY} />
        </Protected>
      }
    />
    <Route
      path="/report-center"
      element={
        <Protected userType="k12_admin">
          <ReportCenter mode={CONTEXT_MODE_SECONDARY} />
        </Protected>
      }
    />
    <Route
      path="/el/experience-tracking/*"
      element={
        <Protected userType="k12_admin">
          <StaffExperienceTrackingPage mode={CONTEXT_MODE_ELEMENTARY} />
        </Protected>
      }
    />
    <Route
      path="/experience-tracking/*"
      element={
        <Protected userType="k12_admin">
          <StaffExperienceTrackingPage mode={CONTEXT_MODE_SECONDARY} />
        </Protected>
      }
    />
    <Route
      path="/student-experience-supervision"
      element={
        <Protected userType="k12_admin">
          <SupervisorExperienceTrackingPage />
        </Protected>
      }
    />
    <Route
      path="/meetings/*"
      element={
        <Protected userType="k12_admin">
          <CounselorMeetingScheduler />
        </Protected>
      }
    />
    <Route
      path="/survey-bank-management/*"
      element={
        <Protected userType="k12_admin">
          <SurveyBankManagement />
        </Protected>
      }
    />
    <Route
      path="/recommendation-letter-portal"
      element={
        <Protected userType="k12_admin">
          <RecommendationLetterPortal />
        </Protected>
      }
    />
    <Route
      path="/scholarship-matching"
      element={
        <Protected userType="k12_admin">
          <ScholarshipMatching />
        </Protected>
      }
    />
    <Route
      path="/award-letter-verification"
      element={
        <Protected userType="k12_admin">
          <AwardLetterVerification />
        </Protected>
      }
    />
    <Route
      path="/industry-partner-database"
      element={<Navigate to="/industry-partner-database/contacts" />}
    />
    <Route
      path="/industry-partner-database/*"
      element={
        <Protected userType="k12_admin">
          <IndustryPartnerCRM />
        </Protected>
      }
    />
    <Route
      path="/el/industry-partner-database"
      element={<Navigate to="/el/industry-partner-database/contacts" />}
    />
    <Route
      path="/el/industry-partner-database/*"
      element={
        <Protected userType="k12_admin">
          <IndustryPartnerCRM />
        </Protected>
      }
    />
    <Route
      path="/configuration-status-report"
      element={
        <Protected userType="k12_admin">
          <ConfigurationStatusReport />
        </Protected>
      }
    />
    <Route
      path="/:casefileName/:studentId/*"
      element={
        <Protected userType="k12_admin">
          <StudentCasefile />
        </Protected>
      }
    />
    <Route
      path="/:casefileName/:studentId"
      element={
        <Protected userType="k12_admin">
          <StudentCasefile />
        </Protected>
      }
    />
    <Route
      path="/program-exploration"
      element={
        <Protected userType="k12_admin">
          <StaffProgramsDashboard />
        </Protected>
      }
    />
    <Route
      path="/program-exploration/*"
      element={
        <Protected userType="k12_admin">
          <IPPEK12AdminRoutes />
        </Protected>
      }
    />
    <Route
      path="/candidate-sourced/:placementHashId/:programId"
      element={
        <Protected userType="k12_admin">
          <BYOPSponsorView />
        </Protected>
      }
    />
    <Route
      path="/org/:orgId/*"
      element={
        <Protected userType="k12_admin">
          <OrgRoutes />
        </Protected>
      }
    />
    <Route
      path="/events/*"
      element={
        <Protected userType="k12_admin">
          <EventScheduler />
        </Protected>
      }
    />
    <Route
      path="/brag-sheet-survey/*"
      element={
        <Protected userType="k12_admin">
          <BragSheetSurvey />
        </Protected>
      }
    />
    <Route
      path="/cte-status-dashboard/*"
      element={
        <Protected userType="k12_admin">
          <CTEProgramsDashboard />
        </Protected>
      }
    />
    <Route
      path="/event-scheduler/*"
      element={<Navigate to={EVENT_SCHEDULE} />}
    />
    <Route
      path="/credential-wallet/:studentId/*"
      element={<CredentialWallet mode="public" />}
    />
    <Route
      path="/public-placement/:placementHashId/:programId"
      element={<Navigate to="/access-denied" />}
    />
    <Route
      path="/college-athletic-eligibility/tabular-view"
      element={
        <Protected userType="k12_admin">
          <CollegeAthleticEligibility />
        </Protected>
      }
    />
    <Route
      path="/sitemap"
      element={
        <Protected userType="k12_admin">
          <Sitemap />
        </Protected>
      }
    />
  </>
);

export default CounselorRoutes;
